import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { PortalDropdown, type DropdownItem } from './PortalDropdown';

// Cortex AutocompleteSelect — a searchable, single-select control built on PortalDropdown.
// This is the app-wide replacement for native <select> (DESIGN_LANGUAGE_SPEC §27). It
// supports text filtering and full keyboard navigation through the underlying dropdown.

export interface AutocompleteOption {
  value: string;
  label: string;
  disabled?: boolean | undefined;
}

export interface AutocompleteSelectProps {
  value: string | null;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string | undefined;
  /** Placeholder shown inside the filter input. */
  searchPlaceholder?: string | undefined;
  /** Accessible label for the control. */
  label?: string | undefined;
  disabled?: boolean | undefined;
}

export function AutocompleteSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  label,
  disabled = false,
}: AutocompleteSelectProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo<DropdownItem[]>(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((o) => (q ? o.label.toLowerCase().includes(q) : true))
      .map((o) => ({
        value: o.value,
        disabled: o.disabled,
        label: (
          <span className="flex w-full items-center justify-between gap-2">
            <span>{o.label}</span>
            {o.value === value && (
              <Check className="h-4 w-4 text-indigo-400" aria-hidden="true" />
            )}
          </span>
        ),
      }));
  }, [options, query, value]);

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  const triggerBase =
    'flex min-h-11 w-full items-center justify-between gap-2 rounded-xl border px-4 py-3 text-left text-sm ' +
    'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ' +
    'disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <PortalDropdown
      items={filtered}
      onSelect={handleSelect}
      isOpen={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setQuery('');
          setActiveIndex(0);
        }
      }}
      activeIndex={activeIndex}
      onActiveIndexChange={setActiveIndex}
      menuLabel={label ?? placeholder}
      header={
        <div className="relative mb-1">
          <Search
            className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${
              isDark ? 'text-white/30' : 'text-slate-400'
            }`}
            aria-hidden="true"
          />
          <input
            type="text"
            value={query}
            autoFocus
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            className={`w-full rounded-lg border py-2 pl-9 pr-3 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
              isDark
                ? 'border-white/[0.06] bg-white/[0.04] text-white placeholder-white/30 focus:border-indigo-500'
                : 'border-slate-200 bg-slate-50 text-slate-900 placeholder-slate-400 focus:border-indigo-500'
            }`}
          />
        </div>
      }
      emptyContent={
        <div
          className={`px-3 py-6 text-center text-sm ${
            isDark ? 'text-white/40' : 'text-slate-400'
          }`}
        >
          No matches
        </div>
      }
      trigger={({ toggle, ref, triggerProps }) => (
        <button
          ref={ref}
          type="button"
          disabled={disabled}
          onClick={toggle}
          aria-label={label ?? placeholder}
          {...triggerProps}
          className={`${triggerBase} ${
            isDark
              ? 'border-white/[0.06] bg-white/[0.04] text-white hover:bg-white/[0.06]'
              : 'border-slate-200 bg-slate-50 text-slate-900 hover:bg-white'
          }`}
        >
          <span className={selected ? '' : isDark ? 'text-white/40' : 'text-slate-400'}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${
              open ? 'rotate-180' : ''
            } ${isDark ? 'text-white/40' : 'text-slate-400'}`}
            aria-hidden="true"
          />
        </button>
      )}
    />
  );
}
